import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Pagination, Select, SelectItem, Tag, TextArea, TextInput, Toggle } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { InlineError, PageHeader } from '../components/states';
import { CURRENCY, Money, When } from '../components/format';
import { MoneyInput } from '../components/MoneyInput';

// Lifecycle triggers (P1-D24): the worker sends an inbox message with a one-use personal giftcode to
// each player who meets a trigger, once per player and period, then tracks deposit or return.

const IDS = ['no_deposit', 'inactive_3', 'inactive_7', 'inactive_14', 'big_loss', 'vip_up'] as const;
type Id = (typeof IDS)[number];
type Trigger = { on: boolean; amount: number; wageringX: number | null; codeDays: number; param: number; title: string; body: string };
type Settings = {
  'crm.triggers': Record<Id, Trigger>; 'crm.vipLevels': number[]; 'crm.conversionDays': number; 'crm.catchUpHours': number; 'crm.batch': number;
};
type Report = { trigger: Id; sent: number; redeemed: number; cost: number; deposited: number; depositSum: number; returned: number; converted: number; conversionPct: number | null };
type Send = {
  id: string; trigger: Id; gameUserId: number; nickname: string | null; period: string; sentAt: string; amount: string; code: string | null;
  redeemed: boolean | null; depositedAt: string | null; depositAmount: string | null; returnedAt: string | null; windowEndsAt: string;
};

const NAME: Record<Id, string> = {
  no_deposit: 'Đăng ký chưa nạp', inactive_3: 'Vắng 3 ngày', inactive_7: 'Vắng 7 ngày', inactive_14: 'Vắng 14 ngày',
  big_loss: 'Thua lớn trong tuần', vip_up: 'Lên hạng VIP',
};
// what `param` means per trigger; vip_up has none (the levels are a separate setting)
const PARAM: Partial<Record<Id, { label: string; money?: boolean }>> = {
  no_deposit: { label: 'Gửi sau khi đăng ký (giờ)' },
  inactive_3: { label: 'Số ngày vắng mặt' }, inactive_7: { label: 'Số ngày vắng mặt' }, inactive_14: { label: 'Số ngày vắng mặt' },
  big_loss: { label: `Thua ròng trong tuần từ (${CURRENCY.symbol})`, money: true },
};
const digits = (s: string) => s.replace(/\D/g, '').slice(0, 13);

function TriggerDialog({ id, t, onClose }: { id: Id; t: Trigger; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ ...t, amount: String(t.amount), wageringX: t.wageringX == null ? '' : String(t.wageringX), codeDays: String(t.codeDays), param: String(t.param) });
  const all = qc.getQueryData<{ settings: Settings }>(['crm-triggers'])?.settings['crm.triggers'];
  const save = useMutation({
    mutationFn: () => api.put('/api/admin/crm/settings/crm.triggers', {
      value: {
        ...all,
        [id]: { on: f.on, amount: Number(f.amount), wageringX: f.wageringX === '' ? null : Number(f.wageringX), codeDays: Number(f.codeDays), param: Number(f.param), title: f.title.trim(), body: f.body.trim() },
      },
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['crm-triggers'] }); onClose(); },
  });
  const p = PARAM[id];
  const ok = f.amount !== '' && f.codeDays !== '' && Number(f.codeDays) >= 1 && f.param !== '' && Number(f.param) >= 1 && f.title.trim() && f.body.trim();
  return (
    <Modal open modalHeading={NAME[id]} primaryButtonText="Lưu" secondaryButtonText="Hủy" primaryButtonDisabled={!ok || !all || save.isPending}
      onRequestClose={onClose} onRequestSubmit={() => save.mutate()}>
      <div className="stack">
        <Toggle id="tr-on" labelText="Trạng thái" labelA="Tắt" labelB="Bật" toggled={f.on} onToggle={(v) => setF({ ...f, on: v })} />
        {p ? (p.money
          ? <MoneyInput id="tr-p" labelText={p.label} maxDigits={13} value={f.param} onChange={(v) => setF({ ...f, param: v })} />
          : <TextInput id="tr-p" labelText={p.label} inputMode="numeric" value={f.param} onChange={(e) => setF({ ...f, param: digits(e.target.value) })} />) : null}
        <div className="grid2">
          <MoneyInput id="tr-a" labelText={`Giá trị mã quà (${CURRENCY.symbol})`} helperText="0 = chỉ gửi thư, không kèm mã" maxDigits={13}
            value={f.amount} onChange={(v) => setF({ ...f, amount: v })} />
          <TextInput id="tr-x" labelText="Vòng cược (lần giá trị mã)" helperText="Để trống = mặc định của giftcode" inputMode="numeric"
            value={f.wageringX} onChange={(e) => setF({ ...f, wageringX: digits(e.target.value).slice(0, 3) })} />
          <TextInput id="tr-d" labelText="Mã hết hạn sau (ngày)" inputMode="numeric" value={f.codeDays} onChange={(e) => setF({ ...f, codeDays: digits(e.target.value).slice(0, 2) })} />
        </div>
        <TextInput id="tr-t" labelText="Tiêu đề thư" maxLength={160} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <TextArea id="tr-b" labelText="Nội dung thư" rows={4} maxCount={2000} enableCounter value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })}
          helperText="{ten} = tên người chơi, {ma} = mã quà, {tien} = giá trị mã, {han} = hạn dùng mã" />
        <InlineError error={save.error} />
      </div>
    </Modal>
  );
}

function GeneralDialog({ s, onClose }: { s: Settings; onClose: () => void }) {
  const qc = useQueryClient();
  const [levels, setLevels] = useState(s['crm.vipLevels'].join(', '));
  const [conv, setConv] = useState(String(s['crm.conversionDays']));
  const [catchUp, setCatchUp] = useState(String(s['crm.catchUpHours']));
  const [batch, setBatch] = useState(String(s['crm.batch']));
  const save = useMutation({
    mutationFn: async () => {
      const lv = levels.split(/[,;\s]+/).filter(Boolean).map((x) => Number(x.replace(/\D/g, '')));
      const put = (k: string, v: unknown) => api.put('/api/admin/crm/settings/' + k, { value: v });
      if (JSON.stringify(lv) !== JSON.stringify(s['crm.vipLevels'])) await put('crm.vipLevels', lv);
      if (Number(conv) !== s['crm.conversionDays']) await put('crm.conversionDays', Number(conv));
      if (Number(catchUp) !== s['crm.catchUpHours']) await put('crm.catchUpHours', Number(catchUp));
      if (Number(batch) !== s['crm.batch']) await put('crm.batch', Number(batch));
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['crm-triggers'] }); onClose(); },
    onError: () => qc.invalidateQueries({ queryKey: ['crm-triggers'] }),
  });
  return (
    <Modal open modalHeading="Cài đặt chung" primaryButtonText="Lưu" secondaryButtonText="Hủy" primaryButtonDisabled={save.isPending}
      onRequestClose={onClose} onRequestSubmit={() => save.mutate()}>
      <div className="stack">
        <TextInput id="cg-lv" labelText="Mốc điểm VIP của từng hạng (tăng dần, cách nhau dấu phẩy)" helperText="Người chơi vượt một mốc mới thì nhận thư Lên hạng VIP."
          value={levels} onChange={(e) => setLevels(e.target.value)} />
        <div className="grid2">
          <TextInput id="cg-cv" labelText="Tính chuyển đổi trong (ngày)" helperText="Nạp hoặc quay lại trong số ngày này sau khi gửi" inputMode="numeric" value={conv} onChange={(e) => setConv(digits(e.target.value).slice(0, 2))} />
          <TextInput id="cg-cu" labelText="Chỉ gửi cho sự kiện trong (giờ)" helperText="Người đủ điều kiện từ lâu hơn thì bỏ qua" inputMode="numeric" value={catchUp} onChange={(e) => setCatchUp(digits(e.target.value).slice(0, 3))} />
          <TextInput id="cg-b" labelText="Số thư tối đa mỗi lượt chạy" inputMode="numeric" value={batch} onChange={(e) => setBatch(digits(e.target.value).slice(0, 4))} />
        </div>
        <InlineError error={save.error} />
      </div>
    </Modal>
  );
}

const sendCols: ColumnDef<Send, any>[] = [
  { header: 'Gửi lúc', accessorKey: 'sentAt', cell: (c) => <When v={c.getValue()} /> },
  { header: 'Kịch bản', accessorKey: 'trigger', cell: (c) => NAME[c.getValue() as Id] ?? c.getValue() },
  { header: 'Người chơi', id: 'u', cell: (c) => <a className="cell-link" href={'/users/' + c.row.original.gameUserId}>{c.row.original.nickname ?? '#' + c.row.original.gameUserId}</a> },
  { header: 'Mã quà', accessorKey: 'code', cell: (c) => c.getValue() ? <span className="num">{c.getValue()}</span> : <span className="muted">Không kèm mã</span> },
  { header: `Giá trị (${CURRENCY.symbol})`, accessorKey: 'amount', cell: (c) => <Money v={c.getValue()} /> },
  { header: 'Đã dùng mã', accessorKey: 'redeemed', cell: (c) => c.getValue() == null ? '-' : c.getValue() ? <Tag size="sm" type="green">Đã dùng</Tag> : <Tag size="sm" type="gray">Chưa</Tag> },
  {
    header: 'Kết quả', id: 'res', enableSorting: false, cell: (c) => {
      const s = c.row.original;
      if (s.depositedAt) return <Tag size="sm" type="green">Nạp <Money v={s.depositAmount ?? 0} /></Tag>;
      if (s.returnedAt) return <Tag size="sm" type="teal">Quay lại chơi</Tag>;
      return Date.parse(s.windowEndsAt) > Date.now() ? <span className="muted">Đang theo dõi</span> : <span className="muted">Không chuyển đổi</span>;
    },
  },
];

function SendLog() {
  const [trigger, setTrigger] = useState('');
  const [page, setPage] = useState({ page: 1, pageSize: 25 });
  const q = useQuery({
    queryKey: ['crm-sends', trigger, page],
    queryFn: () => api.get<{ items: Send[]; total: number }>('/api/admin/crm/sends', { ...page, ...(trigger ? { trigger } : {}) }),
    placeholderData: (p) => p,
  });
  return (
    <DataTable columns={sendCols} data={q.data?.items} loading={q.isLoading} error={q.error} onRetry={() => q.refetch()} rowKey={(s) => s.id}
      title="Nhật ký gửi"
      toolbar={<div className="row" style={{ padding: '0 16px 16px' }}>
        <Select id="sl-t" labelText="Kịch bản" size="sm" value={trigger} onChange={(e) => { setTrigger(e.target.value); setPage({ ...page, page: 1 }); }}>
          <SelectItem value="" text="Tất cả" />
          {IDS.map((i) => <SelectItem key={i} value={i} text={NAME[i]} />)}
        </Select>
      </div>}
      empty={{ title: 'Chưa gửi thư nào', body: 'Bật một kịch bản để hệ thống bắt đầu gửi.' }}
      footer={q.data ? <Pagination page={page.page} pageSize={page.pageSize} pageSizes={[25, 50, 100]} totalItems={q.data.total}
        itemsPerPageText="Mỗi trang" onChange={({ page: p, pageSize }) => setPage({ page: p, pageSize })} /> : null} />
  );
}

export function CrmTriggersPage() {
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['crm-triggers'], queryFn: () => api.get<{ settings: Settings; report: Report[]; from: string; to: string }>('/api/admin/crm/triggers') });
  const [edit, setEdit] = useState<Id | null>(null);
  const [general, setGeneral] = useState(false);
  const s = q.data?.settings;
  const by = new Map((q.data?.report ?? []).map((r) => [r.trigger, r]));
  type Row = { id: Id; t: Trigger; r?: Report };
  const rows: Row[] | undefined = s ? IDS.map((id) => ({ id, t: s['crm.triggers'][id], r: by.get(id) })) : undefined;
  const n = (v: number | undefined) => <span className="num">{(v ?? 0).toLocaleString('vi-VN')}</span>;
  const columns: ColumnDef<Row, any>[] = [
    { header: 'Kịch bản', id: 'name', cell: (c) => NAME[c.row.original.id] },
    { header: 'Trạng thái', id: 'on', cell: (c) => c.row.original.t.on ? <Tag size="sm" type="green">Bật</Tag> : <Tag size="sm" type="gray">Tắt</Tag> },
    { header: `Mã quà (${CURRENCY.symbol})`, id: 'amt', cell: (c) => <Money v={c.row.original.t.amount} /> },
    { header: 'Đã gửi', id: 'sent', cell: (c) => n(c.row.original.r?.sent) },
    { header: 'Dùng mã', id: 'red', cell: (c) => n(c.row.original.r?.redeemed) },
    { header: `Chi phí mã (${CURRENCY.symbol})`, id: 'cost', cell: (c) => <Money v={c.row.original.r?.cost ?? 0} /> },
    { header: 'Nạp', id: 'dep', cell: (c) => <>{n(c.row.original.r?.deposited)} <span className="muted">(<Money v={c.row.original.r?.depositSum ?? 0} />)</span></> },
    { header: 'Quay lại', id: 'ret', cell: (c) => n(c.row.original.r?.returned) },
    { header: 'Chuyển đổi', id: 'pct', cell: (c) => c.row.original.r?.conversionPct == null ? <span className="muted">-</span> : <span className="num">{c.row.original.r.conversionPct.toLocaleString('vi-VN')}%</span> },
  ];
  return (
    <div className="page">
      <PageHeader title="Chăm sóc tự động"
        lead={<>Mỗi kịch bản gửi thư kèm một mã quà dùng một lần, chỉ người nhận nhập được, mỗi người mỗi kỳ một lần. Chuyển đổi = nạp tiền hoặc quay lại chơi trong {s?.['crm.conversionDays'] ?? 7} ngày sau khi gửi. Số liệu 30 ngày gần nhất.</>}
        actions={can('crm.manage') && s ? <Button size="sm" kind="tertiary" onClick={() => setGeneral(true)}>Cài đặt chung</Button> : null} />
      <DataTable columns={columns} data={rows} loading={q.isLoading} error={q.error} onRetry={() => q.refetch()} rowKey={(r) => r.id}
        onRowClick={(r) => setEdit(r.id)} empty={{ title: 'Chưa có kịch bản' }} />
      <div style={{ marginTop: 32 }}><SendLog /></div>
      {edit && s ? <TriggerDialog key={edit} id={edit} t={s['crm.triggers'][edit]} onClose={() => setEdit(null)} /> : null}
      {general && s ? <GeneralDialog s={s} onClose={() => setGeneral(false)} /> : null}
    </div>
  );
}
